import React from 'react';
import { Grid, Box, Typography, useTheme, alpha } from '@mui/material';
import { InvestmentScoreObject } from '@features/analysis/types';
import { StyledCard, StyledCardHeader, StyledCardContent, SummaryMetric, MetricLabel, MetricValue } from '@features/analysis/components/StyledComponents';
import { AnalysisSummaryProps } from '@features/analysis/types';

const AnalysisSummary: React.FC<AnalysisSummaryProps> = ({ analysis }) => {
  const theme = useTheme();

  // Function to determine color based on investment score
  const getInvestmentScoreColor = (scoreData: number | InvestmentScoreObject | undefined | null): string => {
    let score: number | null = null;

    if (typeof scoreData === 'number') {
      score = scoreData;
    } else if (scoreData && typeof scoreData === 'object' && scoreData.score !== undefined && scoreData.score !== null && String(scoreData.score).toUpperCase() !== 'N/A') {
      // Check if score is not "N/A" before parsing
      const parsed = parseFloat(String(scoreData.score));
      if (!isNaN(parsed)) {
        score = parsed;
      }
    }

    // Determine color based on the extracted numeric score
    if (score === null) return theme.palette.grey[500]; // Default for N/A or invalid
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.info.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12}>
        <StyledCard>
          <StyledCardHeader
            title="Analysis Summary"
            subheader="Key metrics and insights at a glance"
          />
          <StyledCardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <SummaryMetric>
                  <MetricLabel>Investment Score</MetricLabel>
                  <MetricValue sx={{
                    color: getInvestmentScoreColor(analysis?.investmentScore || null),
                    fontSize: '1.5rem'
                  }}>
                    {analysis?.investmentScore ?
                      (typeof analysis.investmentScore === 'object' ?
                        analysis.investmentScore.score :
                        analysis.investmentScore
                      ) : 'N/A'
                    }
                  </MetricValue>
                </SummaryMetric>
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryMetric>
                  <MetricLabel>Competitive Position</MetricLabel>
                  <MetricValue sx={{ fontSize: '1.1rem' }}>
                    {analysis.marketAnalysis?.competitivePosition || 'N/A'}
                  </MetricValue>
                </SummaryMetric>
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryMetric>
                  <MetricLabel>ROI Potential</MetricLabel>
                  <MetricValue sx={{ fontSize: '1.1rem' }}>
                    {analysis.valuationInsights?.potentialROI || 'N/A'}
                  </MetricValue>
                </SummaryMetric>
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryMetric>
                  <MetricLabel>Estimated Value Range</MetricLabel>
                  <MetricValue sx={{ fontSize: '0.85rem', lineHeight: 1.3 }}>
                    {analysis.valuationInsights?.estimatedValue?.low && analysis.valuationInsights?.estimatedValue?.high ?
                      `$${analysis.valuationInsights.estimatedValue.low.toLocaleString()} - $${analysis.valuationInsights.estimatedValue.high.toLocaleString()}` : 'N/A'
                    }
                  </MetricValue>
                </SummaryMetric>
              </Grid>
            </Grid>
          </StyledCardContent>
        </StyledCard>
      </Grid>
    </Grid>
  );
};

export default AnalysisSummary;